import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  GenAITransformActionsContextType,
  GenAITransformStateContextType,
} from './GenAITransformContext.types';
import useRecipeBuilderAdapter from 'new/state/recipeBuilder/adapter/useRecipeBuilderAdapter';
import useRecipeBuilderRepository from 'new/state/recipeBuilder/repository/useRecipeBuilderRepository';
import { parseDSLCodeArray } from 'js/lib/TransformDSLUtils';
import { GEN_AI_ENDPOINTS } from 'js/components/elements/GenAiChat/api/streamFetch';
import { tryCatch } from 'new/utils/TryCatch';

const SUPPORTED_TYPE = true;
const UNSUPPORTED_TYPE = false;
const UNSUPPORTED_TX_TYPES = ['tx_aggregate'];
const UNSUPPORTED_LANGUAGE_TYPES = ['javascript', 'jolt_custom'];

export const GenAITransformStateContext = createContext<GenAITransformStateContextType>({
  isGenAiChatOpen: false,
  endpoint: GEN_AI_ENDPOINTS.GENAI_TX_DSL,
  selectedCodeTypeOption: null,
  disabled: false,
});

export const useGenAITransformState = (): GenAITransformStateContextType => {
  const context = useContext(GenAITransformStateContext);
  if (!context) {
    throw new Error('useGenAITransform must be used within a GenAITransformProvider');
  }
  return context;
};

export const GenAITransformActionsContext = createContext<GenAITransformActionsContextType>({
  getCurrentContext: () => ({ input_context: [] }),
  toggleGenAIChat: () => {},
  handleGenAIOnCodeSelected: () => (data: string | string[]) => {},
});

export const useGenAITransformActions = (): GenAITransformActionsContextType => {
  const context = useContext(GenAITransformActionsContext);
  if (!context) {
    throw new Error('useGenAITransform must be used within a GenAITransformProvider');
  }
  return context;
};

type TxBuilderPartial = { type: string; code: string };

type GenAITransformProviderProps = {
  children: React.ReactNode;
  runNexsetRules: () => void;
};

export const GenAITransformProvider = (props: GenAITransformProviderProps) => {
  const [isGenAiChatOpen, setIsGenAiChatOpen] = useState(false);
  const { updateRecipeGroups, expandRecipeGroup } = useRecipeBuilderAdapter();

  const { recipeGroups, getRecipeGroupByType, getRecipesGroupByType, expandedRecipeGroupIndex } =
    useRecipeBuilderRepository();

  const { group: txCode, index: txCodeIndex } = getRecipeGroupByType('tx_code');

  const selectedCodeTypeOption = txCode
    ? {
        label: txCode.data.codeLanguage,
        value: txCode.data.codeLanguage,
        type: txCode.data.codeLanguage,
      }
    : null;

  const supportedType = recipeGroups.length
    ? recipeGroups?.every((group) => {
        if (UNSUPPORTED_TX_TYPES.includes(group.type)) return UNSUPPORTED_TYPE;

        if (group?.type === 'tx_code') {
          if (UNSUPPORTED_LANGUAGE_TYPES.includes(group.data.codeLanguage)) return UNSUPPORTED_TYPE;

          if (group?.data?.type === 'reusable_tx') return UNSUPPORTED_TYPE;
        }

        return SUPPORTED_TYPE;
      })
    : SUPPORTED_TYPE; // if there is no data, the GenAI button should be enabled

  const getCurrentContext = () => {
    if (txCode) {
      return { input_context: txCode?.data?.code ?? '' };
    }

    const expandedRecipeGroup = recipeGroups[expandedRecipeGroupIndex];

    if (expandedRecipeGroup && expandedRecipeGroup.type === 'tx_builder') {
      const { data } = expandedRecipeGroup;
      const ctx = data?.map((tx) => tx?.code) ?? [];
      return { input_context: ctx };
    }

    return { input_context: [] };
  };

  const toggleGenAIChat = useCallback(() => {
    setIsGenAiChatOpen((state) => !state);
  }, []);

  const autoRunNexsetRules = useCallback(() => {
    // execute on the next event cycle
    setTimeout(() => {
      props?.runNexsetRules();
    }, 0);
  }, [props?.runNexsetRules]);

  const handleSuggestedCodeByGenAI = (suggestedCode: string) => {
    if (typeof suggestedCode !== 'string') {
      console.warn('Suggested code is not a string');
      return;
    }

    if (!txCode || txCodeIndex === undefined) {
      const newTxCode = {
        type: 'tx_code',
        data: {
          type: 'custom_code',
          codeLanguage: 'python',
          code: suggestedCode,
        },
      };
      updateRecipeGroups([newTxCode], true);
      autoRunNexsetRules();
      return;
    }

    const newRecipeGroups = [...recipeGroups];
    newRecipeGroups[txCodeIndex] = {
      ...txCode,
      data: {
        ...txCode.data,
        code: suggestedCode,
      },
    };
    updateRecipeGroups(newRecipeGroups, true);
    autoRunNexsetRules();
  };

  const createNewTransformCodeGroup = (newRules: TxBuilderPartial[]) => {
    const newRecipeGroups = [
      ...recipeGroups,
      {
        type: 'tx_builder',
        data: newRules,
      },
    ];
    updateRecipeGroups(newRecipeGroups, true);
    expandRecipeGroup(recipeGroups.length);
    autoRunNexsetRules();
  };

  /**
   * 1. Check if there is any tx_builder group
   * 2. If there is:
   *    - Check if the expanded recipe group index is tx_builder and update the code in that group
   *    - If it is not, create a new one
   * 3. If there is, and there is not an expanded recipe group index:
   *    - Create a new tx_builder group
   * 4. If there is not:
   *    - Create a new tx_builder group
   */
  const handleSuggestedTxDSLByGenAI = (suggested: string[]) => {
    /**
     * If the user clicks on `apply` and there is no data on the screen, apply the code.
     */
    if (!Array.isArray(suggested) && recipeGroups.length === 0) {
      handleSuggestedCodeByGenAI(suggested);
      return;
    }

    const txBuilderGroups = getRecipesGroupByType('tx_builder');
    const isEmpty = txBuilderGroups.length === 0;

    const { data: newRules, error: errorParsingDSLCode } = tryCatch(() =>
      parseDSLCodeArray(suggested),
    );

    if (errorParsingDSLCode) {
      console.warn('Error parsing suggested DSL code: \n', errorParsingDSLCode?.message);
      return;
    }

    if (isEmpty) {
      createNewTransformCodeGroup(newRules);
      return;
    }

    const expandedRecipeGroup = recipeGroups[expandedRecipeGroupIndex];

    if (!expandedRecipeGroup || expandedRecipeGroup.type !== 'tx_builder') {
      createNewTransformCodeGroup(newRules);
      return;
    }

    const updatedTxGroup = { ...expandedRecipeGroup, data: newRules };

    // if there are no changes to be persisted
    if (JSON.stringify(expandedRecipeGroup.data) === JSON.stringify(newRules)) {
      return;
    }

    const newRecipeGroups = [...recipeGroups];
    newRecipeGroups[expandedRecipeGroupIndex] = updatedTxGroup;

    updateRecipeGroups(newRecipeGroups, true);
    autoRunNexsetRules();
  };

  /**
   * @Note:
   * - If you want to disable specific endpoint, i.e., GEN_AI_CODEGEN
   * - you can do it by setting the feature flag and handle it on the codebase below
   *
   * > endpoint
   * > genAIHandlerForSelectedCode
   */
  const endpoint = useMemo(() => {
    return txCode ? GEN_AI_ENDPOINTS.GENAI_CODEGEN : GEN_AI_ENDPOINTS.GENAI_TX_DSL;
  }, [txCode]);

  const genAIHandlerForSelectedCode = (() => {
    if (endpoint === GEN_AI_ENDPOINTS.GENAI_CODEGEN) return handleSuggestedCodeByGenAI;
    if (endpoint === GEN_AI_ENDPOINTS.GENAI_TX_DSL) return handleSuggestedTxDSLByGenAI;

    return () => {
      console.warn('No endpoint found to handle suggested code by GenAI');
    };
  })();

  /**
   * If the user selects a not supported code type and the GenAI chat is open, close the chat.
   */
  useEffect(() => {
    if (!supportedType && isGenAiChatOpen) {
      setIsGenAiChatOpen(false);
    }
  }, [supportedType]);

  const memoizedStateValue = useMemo(
    () => ({
      endpoint,
      isGenAiChatOpen,
      selectedCodeTypeOption,
      disabled: !supportedType,
    }),
    [endpoint, isGenAiChatOpen, selectedCodeTypeOption, supportedType],
  );

  const memoizedActionsValue = useMemo(
    () => ({
      getCurrentContext,
      toggleGenAIChat,
      handleGenAIOnCodeSelected: genAIHandlerForSelectedCode,
    }),
    [toggleGenAIChat, genAIHandlerForSelectedCode, getCurrentContext],
  );

  return (
    <GenAITransformStateContext.Provider value={memoizedStateValue}>
      <GenAITransformActionsContext.Provider value={memoizedActionsValue}>
        {props?.children}
      </GenAITransformActionsContext.Provider>
    </GenAITransformStateContext.Provider>
  );
};
